import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import ReactPlayer from "react-player";
import clsx from "clsx";

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { setCurrentPreviewVideo, setPreviewPlaying } from "../../store/player/playerSlice";

import { useHlsMedia } from "../../hooks/Videos/useHlsStreaming";
import styles from "./VideoPlayerPreview.module.css";
import { IData } from "../Cards/types";

interface IProps {
    data?: IData[] | null;
    currentSlideIndex: number;
}

// Detect iOS Safari — it handles HLS natively and must NOT use HLS.js
const isIOSSafari = (): boolean => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent;
    return /iP(hone|ad|od)/.test(ua) && /WebKit/.test(ua) && !/CriOS|FxiOS|OPiOS/.test(ua);
};

const VideoPlayerPreview: React.FC<IProps> = ({ data, currentSlideIndex }) => {
    const dispatch = useDispatch();
    const playerRef = useRef<ReactPlayer | null>(null);

    const { isPlay, isPreviewPlaying, play: isMainVideoPlaying, isVideoPlayerOpen, currentPreviewVideoId } =
        useSelector((state: RootState) => state.videoPlayer);

    const [showVideo, setShowVideo] = useState(false);
    const [isReady, setIsReady] = useState(false);
    // iOS autoplay REQUIRES muted=true at all times initially — never flip to false until user gesture
    const [muted, setMuted] = useState(true);

    const hasSeekedRef = useRef(false);
    const isIOS = useRef(isIOSSafari());
    const { handleHls } = useHlsMedia(playerRef);

    const videoData = useMemo(() => data?.[currentSlideIndex] || null, [data, currentSlideIndex]);

    const handlePreviewReady = useCallback(() => {
        setIsReady(true);

        const video = playerRef.current?.getInternalPlayer?.() as HTMLVideoElement | null;
        if (!video) return;

        // Preview player should never expose remote playback / AirPlay.
        video.disableRemotePlayback = true;
        video.setAttribute("disableremoteplayback", "");
        video.setAttribute("x-webkit-airplay", "deny");
        video.setAttribute("airplay", "deny");
    }, []);

    // ─── Early returns BEFORE hooks — must be consistent ─────────────────────
    // NOTE: Hooks above must always run; conditionals must come after all hooks.

    // Reset on slide change
    useEffect(() => {
        setShowVideo(false);
        setIsReady(false);
        setMuted(true);
        hasSeekedRef.current = false;
        dispatch(setPreviewPlaying(false));
    }, [currentSlideIndex, dispatch]);

    // Delay then show video
    useEffect(() => {
        if (!videoData?.id) return;

        dispatch(setCurrentPreviewVideo(videoData.id));

        const timer = setTimeout(() => {
            setShowVideo(true);
            dispatch(setPreviewPlaying(true));
        }, 5000);

        return () => clearTimeout(timer);
    }, [videoData?.id, dispatch]);

    // Seek to 220s after ready — only for non-HLS or non-iOS
    useEffect(() => {
        if (!isReady || !isPreviewPlaying || hasSeekedRef.current) return;
        if (!playerRef.current) return;

        // Skip this for iOS HLS — handleHls manages it separately
        if (isIOS.current && videoData?.preview_video?.includes(".m3u8")) return;

        hasSeekedRef.current = true;
        const timer = setTimeout(() => {
            playerRef.current?.seekTo(220, "seconds");
            console.log("⏩ Seeked to 220 seconds");
        }, 300);

        return () => clearTimeout(timer);
    }, [isReady, isPreviewPlaying, videoData?.preview_video]);

    // HLS.js handling — ONLY for non-iOS (iOS handles HLS natively via <video>)
    useEffect(() => {
        if (!isReady || !isPreviewPlaying || hasSeekedRef.current) return;
        if (!videoData?.preview_video?.includes(".m3u8")) return;
        if (isIOS.current) return; // iOS Safari: let native HLS handle it

        hasSeekedRef.current = true;
        handleHls(videoData.preview_video);
    }, [isReady, isPreviewPlaying, videoData?.preview_video, handleHls]);

    // Unlock sound on first user interaction
    useEffect(() => {
        const enableSound = () => setMuted(false);
        document.addEventListener("click", enableSound, { once: true });
        return () => document.removeEventListener("click", enableSound);
    }, []);

    if (!videoData) return null;
    if (!isPlay || isMainVideoPlaying || isVideoPlayerOpen) return null;

    const isActiveSlideVideo = currentPreviewVideoId === videoData.id;

    const playerConfig = isIOS.current
        ? {
            file: {
                forceHLS: false,
                forceSafariHLS: false,
                attributes: {
                    playsInline: true,
                    "webkit-playsinline": "true",
                    muted: true,
                    autoPlay: true,
                    disableRemotePlayback: true,
                    disableremoteplayback: true,
                    "x-webkit-airplay": "deny",
                    airplay: "deny",
                },
            },
        }
        : {
            file: {
                forceHLS: true,
                forceSafariHLS: false,
                attributes: {
                    playsInline: true,
                    disableRemotePlayback: true,
                    disableremoteplayback: true,
                },
            },
        };

    return (
        <div className={clsx(styles.video, isPreviewPlaying && isActiveSlideVideo && styles.active)}>
            {showVideo && (
                <ReactPlayer
                    ref={playerRef}
                    url={videoData.preview_video}
                    playing={isPreviewPlaying && isActiveSlideVideo}
                    muted={muted}
                    loop
                    width="100%"
                    height="100%"
                    style={{ objectFit: "cover" }}
                    playsInline
                    className={styles.player}
                    onReady={handlePreviewReady}
                    config={playerConfig}
                />
            )}
        </div>
    );
};

export default VideoPlayerPreview;
